import React, { createContext, useState, useContext, useEffect } from 'react';
import { DEFAULT_LANGUAGE_CODES } from '../i18n/languageMeta';
import enTranslations from '../locales/en.json';
import svTranslations from '../locales/sv.json';

const STATIC_TRANSLATIONS = {
  en: enTranslations,
  sv: svTranslations
};

const translationCache = { ...STATIC_TRANSLATIONS };
const translationRequests = {};
const KNOWN_LANGUAGE_CODES = new Set(DEFAULT_LANGUAGE_CODES);

const normalizeLanguageCode = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .split('-')[0];

const hasKnownLanguage = (value) => KNOWN_LANGUAGE_CODES.has(normalizeLanguageCode(value));

const getPublicLocaleUrl = (code) => {
  const base = String(process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/locales/${code}.json`;
};

const loadTranslations = async (value) => {
  const code = normalizeLanguageCode(value);
  if (!hasKnownLanguage(code)) {
    return null;
  }

  if (translationCache[code]) {
    return translationCache[code];
  }

  if (translationRequests[code]) {
    return translationRequests[code];
  }

  const request = fetch(getPublicLocaleUrl(code), {
    cache: 'force-cache'
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Locale fetch failed for ${code}: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      const resolved = data && typeof data === 'object' ? data : {};
      translationCache[code] = resolved;
      return resolved;
    })
    .catch(() => translationCache[code] || STATIC_TRANSLATIONS[code] || null)
    .finally(() => {
      delete translationRequests[code];
    });

  translationRequests[code] = request;
  return request;
};

const resolveNestedValue = (source, key) => {
  const keys = String(key || '').split('.');
  let value = source;

  for (const currentKey of keys) {
    value = value?.[currentKey];
  }

  return value;
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const getInitialLanguage = () => {
    if (typeof window === 'undefined') return 'en';

    try {
      const params = new URLSearchParams(window.location.search || '');
      const paramLang =
        params.get('lang') ||
        params.get('language') ||
        params.get('locale') ||
        params.get('lp_lang') ||
        params.get('lpLang');
      const paramCode = normalizeLanguageCode(paramLang);
      if (hasKnownLanguage(paramCode)) return paramCode;
    } catch {
    }

    const stored =
      localStorage.getItem('itraxx_language') ||
      localStorage.getItem('userLanguage') ||
      localStorage.getItem('i18nextLng');

    const storedCode = normalizeLanguageCode(stored);
    if (hasKnownLanguage(storedCode)) return storedCode;

    const browserCandidates = [
      ...(Array.isArray(navigator.languages) ? navigator.languages : []),
      navigator.language,
      navigator.userLanguage
    ].filter(Boolean);
    for (const lang of browserCandidates) {
      const code = normalizeLanguageCode(lang);
      if (hasKnownLanguage(code)) return code;
    }

    return 'en';
  };

  const [language, setLanguage] = useState(getInitialLanguage);
  const [companySettings, setCompanySettings] = useState(null);
  const [loadedTranslations, setLoadedTranslations] = useState(() => ({ ...STATIC_TRANSLATIONS }));

  useEffect(() => {
    let cancelled = false;

    const ensureLanguageLoaded = async () => {
      const resolved = await loadTranslations(language);
      if (!cancelled && resolved) {
        setLoadedTranslations((prev) => {
          if (prev[language] === resolved) {
            return prev;
          }
          return {
            ...prev,
            [language]: resolved
          };
        });
      }
    };

    ensureLanguageLoaded();

    return () => {
      cancelled = true;
    };
  }, [language]);

  const t = (key, options = {}) => {
    let value = resolveNestedValue(loadedTranslations[language], key);

    if (!value && language !== 'en') {
      value = resolveNestedValue(loadedTranslations.en, key);
    }

    if (!value) return key;

    if (typeof value === 'object' && options.count !== undefined) {
      const count = options.count;

      if (count === 0 && value.zero) {
        value = value.zero;
      } else if (count === 1 && value.one) {
        value = value.one;
      } else if (value.other) {
        value = value.other;
      } else {
        value = value.one || value.other || key;
      }
    }

    if (typeof value === 'string' && options) {
      value = value.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, variable) => {
        return options[variable] !== undefined ? options[variable] : match;
      });
    }

    return value;
  };

  const persistLanguage = (newLang) => {
    localStorage.setItem('itraxx_language', newLang);
    localStorage.setItem('userLanguage', newLang);
    document.documentElement.lang = newLang;
  };

  const changeLanguage = (newLang) => {
    const resolved = normalizeLanguageCode(newLang);
    if (!hasKnownLanguage(resolved)) {
      return;
    }

    setLanguage(resolved);
    persistLanguage(resolved);

    if (!loadedTranslations[resolved]) {
      loadTranslations(resolved).then((translations) => {
        if (!translations) return;
        setLoadedTranslations((prev) => ({
          ...prev,
          [resolved]: translations
        }));
      }).catch(() => {});
    }
  };

  const loadCompanySettings = (settings) => {
    setCompanySettings(settings);

    const stored =
      (typeof window !== 'undefined' &&
        (localStorage.getItem('itraxx_language') || localStorage.getItem('userLanguage'))) ||
      null;
    const storedCode = normalizeLanguageCode(stored);
    const companyDefault = normalizeLanguageCode(settings?.language?.default);

    if (!storedCode && hasKnownLanguage(companyDefault)) {
      setLanguage(companyDefault);
      persistLanguage(companyDefault);
    }
  };

  const getAvailableLanguages = () => {
    if (companySettings?.language?.available) {
      return companySettings.language.available
        .map((code) => normalizeLanguageCode(code))
        .filter((code) => hasKnownLanguage(code));
    }
    return DEFAULT_LANGUAGE_CODES.filter((code) => hasKnownLanguage(code));
  };

  const canChangeLanguage = () => {
    return getAvailableLanguages().length > 1;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        changeLanguage,
        t,
        loadCompanySettings,
        getAvailableLanguages,
        canChangeLanguage,
        companySettings
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
